import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Group, Button, Text, Grid, GridCol, Title, Loader } from '@mantine/core';
import { useSelector } from 'react-redux';
import { AppState } from '../../../../client-redux/reducers';
import { Language } from '../../../../languages/languageHandler';
import { ticketInfoLines } from '../../../../../libraries/helpdesk/tickets';
import TicketEditModal from '../../../components/ticket-modal';
import { formatDateTimeLabel } from '../../../../utils/date';
import './styles.scss';

export enum MODE {
  READ = 'read',
  EDIT = 'edit',
  NEW = 'new'
}

interface TicketLinesProps {
  ticketData: any;
  lines: ticketInfoLines[];
  loading: boolean;
  noResults: boolean;
  mode: string;
  determineStatus: (status: number, lang: Language) => string;
  onReload: () => void;
}

const TicketLines: React.FC<TicketLinesProps> = ({ lines, loading, mode, determineStatus, onReload }) => {
  const lang = useSelector<AppState, Language>(state => state.lang.lang);
  const region = useSelector<AppState, string>(state => state.login.user?.region || 'UTC');

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<any>(null);
  const [modalData, setModalData] = useState({ type: '' });
  const { ticketID } = useParams();

  const openModal = (type: string, ticketLine?: any) => {
    let defaultData: Partial<ticketInfoLines> & { ticketID: number } = { ticketID: Number(ticketID) };

    if (type === 'add' && lines.length > 0) {
      const lastLine = lines[lines.length - 1];
      defaultData = {
        ...defaultData,
        status: lastLine.status || 0,
        time: lastLine.time || 0,
        user: lastLine.user || null
      };
    }

    if (type === 'update' && ticketLine) {
      defaultData = { ...defaultData, ...ticketLine };
    }

    setModalData({ type });
    setSelectedTicket(defaultData);
    setIsModalOpen(true);
  };

  return (
    <Group className="ticket-line">
      {isModalOpen && (
        <TicketEditModal
          isOpen={isModalOpen}
          hideBtn={() => setIsModalOpen(false)}
          ticketData={selectedTicket}
          mode={modalData.type}
          onReload={onReload}
        />
      )}

      {loading ? (
        <Loader color="#016273" />
      ) : (
        lines.map((ticketLine, index) => (
          <Group
            key={index}
            onClick={mode === MODE.EDIT ? () => openModal('update', ticketLine) : undefined}
            style={{ cursor: mode === MODE.EDIT ? 'pointer' : 'default' }}
            className="ticket-line-details"
          >
            {/* Basic info grid */}
            <Grid className="ticket-line-grid">
              <GridCol className="ticket-line-col" span={{ sm: 12, md: 6, lg: 3 }}>
                <Title order={5}>{`${lang.LOGIN_USER}: `}</Title>
                <Text>
                  {`${ticketLine?.user?.name ?? ''} ${ticketLine?.user?.entid ?? ''} / ${ticketLine?.user?.uid ?? ''}`}
                </Text>
              </GridCol>

              <GridCol className="ticket-line-col" span={{ sm: 12, md: 6, lg: 3 }}>
                <Title order={5}>{`${lang.GLOBAL_DATE}: `}</Title>
                <Text>{formatDateTimeLabel(ticketLine?.statusDT, region)}</Text>
              </GridCol>

              <GridCol className="ticket-line-col" span={{ sm: 12, md: 6, lg: 3 }}>
                <Title order={5}>{`${lang.EQUIPMENT_COM_STATE}: `}</Title>
                <Text>{determineStatus(ticketLine?.status || 0, lang)}</Text>
              </GridCol>

              <GridCol className="ticket-line-col" span={{ sm: 12, md: 6, lg: 3 }}>
                <Title order={5}>{`${lang.GLOBAL_TIME}: `}</Title>
                <Text>{`${ticketLine?.time} min`}</Text>
              </GridCol>
            </Grid>

            {/* Description / comments */}
            <Grid gap="xs" className="description-comments">
              <GridCol span={{ sm: 12, md: 6, lg: 6 }}>
                <Title order={5}>{lang.EQUIPMENT_DESCRIPTION}</Title>
                <Text>{ticketLine.description || '-'}</Text>
              </GridCol>

              <GridCol span={{ sm: 12, md: 6, lg: 6 }}>
                <Title order={5}>{lang.GLOBAL_COMMENTS}</Title>
                <Text>{ticketLine.comments || '-'}</Text>
              </GridCol>
            </Grid>
          </Group>
        ))
      )}

      {mode === MODE.EDIT && (
        <Button radius="md" className="update-status-button" color="#538539" onClick={() => openModal('add')}>
          {lang.HELPDESK_TICKET_ADD_LINE}
        </Button>
      )}
    </Group>
  );
};

export default TicketLines;
